/**
 * AI Chatbot Iframe Readiness Detection Service
 *
 * This service provides reliable detection of when the AI chatbot iframe
 * is ready to receive PostMessage communications.
 */

// Default readiness getter — GlobalChatbotContainer's docked iframe.
const DEFAULT_READINESS_GETTER = 'aiChatbot/isIframeReady'

/**
 * Checks if the iframe is ready using Vuex store state
 * @param {Function} rootGetters - Vuex rootGetters for accessing store state
 * @param {string} readinessGetter - Vuex getter path whose truthiness means ready.
 *   AHA2-146: callers targeting the native Slide-agent iframe pass
 *   'aiChatbot/isSlideAgentIframeReady' instead of the default docked-iframe getter.
 * @returns {boolean} True if Vuex indicates the iframe is ready
 */
const isVuexIframeReady = (rootGetters, readinessGetter = DEFAULT_READINESS_GETTER) => {
  return rootGetters?.[readinessGetter] || false
}

/**
 * AI Chatbot Readiness Detection Service
 * Provides methods to detect when AI chatbot iframe is ready
 */
class AIChatbotReadinessService {
  /**
   * Waits for the target iframe's readiness Vuex state to become true
   * @param {Function} rootGetters - Vuex rootGetters for accessing store state
   * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 20000)
   * @param {string} readinessGetter - Vuex getter path to watch (default: the docked
   *   iframe's 'aiChatbot/isIframeReady'). AHA2-146.
   * @returns {Promise<boolean>} True if ready within timeout, false otherwise
   */
  static waitForIframeReady(rootGetters, maxWaitTime = 20000, readinessGetter = DEFAULT_READINESS_GETTER) {
    return new Promise((resolve) => {
      // Check immediately first to avoid unnecessary delay
      if (isVuexIframeReady(rootGetters, readinessGetter)) {
        resolve(true)
        return
      }

      const startTime = Date.now()
      const checkInterval = setInterval(() => {
        if (isVuexIframeReady(rootGetters, readinessGetter)) {
          clearInterval(checkInterval)
          resolve(true)
        } else if (Date.now() - startTime > maxWaitTime) {
          clearInterval(checkInterval)
          resolve(false)
        }
      }, 100)
    })
  }
}

export default AIChatbotReadinessService
